import { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'
import type { StoryItem } from '../../types/api'
import styles from './LowImpactPanel.module.css'

interface LowImpactPanelProps {
  stories: StoryItem[]
}

function renderMarkdownInline(md: string): string {
  const raw = marked.parseInline(md) as string
  return DOMPurify.sanitize(raw)
}

interface LowImpactCardProps {
  story: StoryItem
}

function LowImpactCard({ story }: LowImpactCardProps) {
  const { lang = 'en', city = '' } = useParams<{ lang: string; city: string }>()
  const navigate = useNavigate()

  const titleHtml = useMemo(
    () => renderMarkdownInline(story.ai_title || ''),
    [story.ai_title],
  )

  function handleClick() {
    navigate(`/${lang}/${city}/story/${story.cluster_id}`)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') handleClick()
  }

  return (
    <article
      className={styles.card}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label={story.ai_title}
    >
      {story.image_url && (
        <img
          src={story.image_url}
          alt=""
          className={styles.cardImage}
        />
      )}
      <div className={styles.cardBody}>
        <h3
          className={styles.cardTitle}
          dangerouslySetInnerHTML={{ __html: titleHtml }}
        />
      </div>
    </article>
  )
}

export default function LowImpactPanel({ stories }: LowImpactPanelProps) {
  const { t } = useTranslation()

  if (stories.length === 0) return null

  return (
    <section className={styles.panel} aria-label={t('low_impact_stories')}>
      <h2 className={styles.heading}>{t('low_impact_stories')}</h2>
      <div className={styles.grid}>
        {stories.map((story) => (
          <LowImpactCard key={story.cluster_id} story={story} />
        ))}
      </div>
    </section>
  )
}
